<template>
    <div v-if="isLoading" class="loading-overlay">
        <p>Carregando...</p>
    </div>

    <div
        v-if="msgAlerta"
        class="info-alert row-space clickabe"
        role="alert"
        @click="msgAlerta = null"
    >
        <span class="text">{{ msgAlerta }}</span>
    </div>

    <div class="form-edit-page">
        <div class="row os-filtros">
            <div class="form-item-rangedata">
                <DateRangeSelect
                    :periodoInicial="'currentMonth'"
                    v-model="dateRange"
                />
            </div>

            <div class="form-item">
                <label class="label-caption" for="edTermoPesquisa">
                    Descrição / Fornecedor / Equipamento
                </label>
                <input
                    id="edTermoPesquisa"
                    v-model="edTermoPesquisa"
                    type="text"
                    maxlength="80"
                    v-uppercase
                />
            </div>

            <div class="form-item">
                <EquipamentoFrame
                    v-model:objSelecionado="equipamentoSelecionado"
                />
            </div>

            <button
                type="button"
                class="btn-small"
                :disabled="isLoading"
                @click="lerDados"
            >
                Ler Dados
            </button>

            <button type="button" class="btn-small" @click="closeForm">
                Voltar
            </button>
        </div>
    </div>

    <section class="os-resultados" :aria-busy="isLoading">
        <div class="os-resumo">
            <h2>Checklists Selcionados</h2>
            <span>{{ lsDados.length }} registro(s)</span>
        </div>

        <div v-if="lsDados.length" class="os-grid">
            <article
                v-for="os in lsDados"
                :key="os.id"
                class="os-card"
            >
                <header class="os-card-cabecalho">
                    <strong class="os-numero">Checklist #{{ os.cdchecklist }}</strong>

                    <span
                        class="os-status"
                        :class="classeStatus(os.nmsituacao)"
                    >
                        {{ os.nmsituacao || 'SEM STATUS' }}
                    </span>
                </header>

                <div class="os-card-corpo">
                    <div class="os-veiculo">
                        <span class="os-legenda">Equipamento</span>
                        <h3>{{ os.nmequipamento || 'Não informado' }}</h3>
                        <span class="os-placa">
                            Placa: {{ os.placa || 'Não informada' }}
                        </span>
                    </div>

                    <div class="os-campo">
                        <span class="os-legenda">Modelo de Checklist</span>
                        <p>{{ os.nmmodelo || 'Não informada' }}</p>
                    </div>

                    <div class="os-campo">
                        <span class="os-legenda">Operador/Usuário</span>
                        <p>{{ os.nmoperador || 'Não informado' }}</p>
                    </div>

                    <div class="os-datas">
                        <div class="info-card info-card--soft">
                            <span>Data</span>
                            <strong>{{ exibirData(os.dtvistoria) }}</strong>
                        </div>

                        <div class="info-card info-card--soft">
                            <span>KM/Horas</span>
                            <strong>{{ Funcoes.formatDecimal(os.kmhoras, 1) }}</strong>
                        </div>
                    </div>
                </div>

                <footer class="os-card-rodape">
                    <button
                        type="button"
                        class="btn-small btn-delete"
                        :disabled="isLoading"
                        :aria-label="`Excluir Checklist ${os.cdchecklist}`"
                        @click="excluirRegistro(os)"
                    >
                        Excluir
                    </button>
                </footer>
            </article>
        </div>

        <div v-else-if="!isLoading" class="info-empty">
            Nenhum checklist para exibir. Informe os filtros e clique em
            “Ler Dados” para consultar.
        </div>
    </section>

    <ConfirmModal ref="confirmModal" />
    <ModalJustificativaTexto ref="modalJustificativa" />
</template>

<script setup>
import { ref } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { Funcoes } from '@/funcoes/utils';
import { globalStore } from '@/globals';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import DateRangeSelect from '@/components/DateRangeSelect.vue';
import EquipamentoFrame from '@/components/frames/EquipamentoFrame.vue';
import ModalJustificativaTexto from '@/components/modals/ModalJustificativaTexto.vue';

const emit = defineEmits(['close']);

const confirmModal = ref(null);
const dateRange = ref({ startDate: "", endDate: "", selectedPeriod: "" });
const edTermoPesquisa = ref(null);
const equipamentoSelecionado = ref();
const isLoading = ref(false);
const lsDados = ref([]);
const modalJustificativa = ref(null);
const msgAlerta = ref(null);


function closeForm(){   
    emit('close');
}


async function excluirRegistro(obj){
    const rModal = await modalJustificativa.value.show({
        titulo: 'Justificativa para exclusão',
        maxlength: 2000,        
    });
    if (!rModal.confirmado) return;


    const confirmado = await confirmModal.value.show({
        message: `Essa ação excluirá definitivamente os dados do checklist nº ${obj.cdos}, do equipamento ${obj.nmequipamento || obj.placa || 'não informado'}. A exclusão será irreversível. Certeza que deseja continuar?`,
        confirmText: 'Sim',
        cancelText: 'Não',
    });
    if (!confirmado){return}


    
    try{
        msgAlerta.value=null;
        isLoading.value=true;
        const ret = await Api.genericPost('/frota/checklist/exclusao', {
            idmunicipio: globalStore.idmunicipio,
            idreferencia: obj.id,
            idlogin: globalStore.idlogin,
            strparam1: rModal.justificativa,
            idusuario: globalStore.idusuario});
        if (ret.bolok === 1){
            await lerDados();
        }else{
            msgAlerta.value=`ATENÇÃO: ${ret.erro}`;
        }
    } catch (error) {msgAlerta.value=`ATENÇÃO: ${error.message}`;
    }finally{isLoading.value=false;}
}


async function lerDados() {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        const payload = {
            idmunicipio: globalStore.idmunicipio,
            idequipamento: equipamentoSelecionado.value?.id,
            datainicial: dateRange.value.startDate,
            datafinal: dateRange.value.endDate,
            idlogin: globalStore.idlogin,
        };

        const req = await Api.genericPost('/dados/checklist', payload);
        lsDados.value = req || [];
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}


// CÓDIGO EXTRA
function exibirData(valor) {
    return valor ? Funcoes.formatDateFromJson(valor) : 'Não informada';
}


function classeStatus(status) {
    switch (String(status || '').trim().toUpperCase()) {
        case 'Finalizado':
            return 'os-status--encerrado';
        default:
            return 'os-status--neutro';
    }
}

</script>

<style scoped>
.os-filtros {
    flex-wrap: wrap;
}

.os-filtros > .form-item {
    flex: 1 1 220px;
    min-width: 0;
}

.os-filtros > .form-item-rangedata {
    flex: 0 1 360px;
    min-width: 0;
    max-width: 100%;
}

.os-filtros > button {
    flex-shrink: 0;
}

.os-resultados {
    margin-top: 18px;
    text-align: left;
}

.os-resumo {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
}

.os-resumo h2 {
    margin: 0;
    color: #25413e;
    font-size: 1rem;
}

.os-resumo > span {
    color: #64748b;
    font-size: 0.85rem;
}

/* Cria duas ou mais colunas quando houver espaço. */
.os-grid {
    display: grid;
    grid-template-columns: repeat(
        auto-fill,
        minmax(min(100%, 360px), 1fr)
    );
    gap: 16px;
    align-items: stretch;
}

.os-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    background: #fff;
    border: 1px solid #cbd8d6;
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(37, 65, 62, 0.06);
}

.os-card-cabecalho {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 14px;
    background: #e6f7f4;
    border-bottom: 1px solid #d9e8e5;
}

.os-numero {
    color: #25413e;
    font-size: 1rem;
}

.os-status {
    max-width: 100%;
    padding: 4px 9px;
    border: 1px solid transparent;
    border-radius: 20px;
    font-size: 0.72rem;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.os-status--encerrado {
    background: #e8eefb;
    color: #304b87;
    border-color: #c9d6f1;
}

.os-status--neutro {
    background: #f1f5f9;
    color: #475569;
    border-color: #cbd5e1;
}

.os-card-corpo {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 14px;
    padding: 14px;
}

.os-legenda {
    display: block;
    margin-bottom: 4px;
    color: #64748b;
    font-size: 0.75rem;
}

.os-veiculo h3 {
    margin: 0;
    color: #25413e;
    font-size: 1rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.os-placa {
    display: inline-block;
    margin-top: 7px;
    padding: 3px 7px;
    border: 1px solid #d5dddf;
    border-radius: 4px;
    background: #f8fafc;
    color: #475569;
    font-size: 0.78rem;
    overflow-wrap: anywhere;
}

.os-campo p {
    margin: 0;
    color: #334155;
    font-size: 0.85rem;
    line-height: 1.5;
    white-space: pre-line;
    overflow-wrap: anywhere;
}

.os-datas {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: auto;
}

.os-valores {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.os-card .info-card {
    min-width: 0;
    padding: 7px 8px;
    border-color: #dbe3e8;
    border-radius: 6px;
}

.os-card .info-card strong {
    font-size: 0.85rem;
}

.os-card-rodape {
    display: flex;
    justify-content: flex-end;
    padding: 10px 14px;
    border-top: 1px solid #e2e8f0;
    background: #fafcfc;
}

@media (max-width: 420px) {
    .os-valores {
        grid-template-columns: 1fr;
    }

    .os-filtros > .form-item-rangedata {
        flex-basis: 100%;
    }
}
</style>